import React from "react";

const SingleProductCard = ({
    product,
    index,
    handleFileUpload,
    handleRemoveImage,
    saveProductField,
    deleteRow,
    fileInputRefs,
    mainImageRefs,
}) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [formState, setFormState] = React.useState({ ...product });

    // Update local state if the product prop changes
    React.useEffect(() => {
        setFormState({ ...product });
    }, [product]);

    const handleChange = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        // Save each field change back to parent using saveProductField
        Object.keys(formState).forEach((field) => {
            saveProductField(index, field, formState[field]);
        });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormState({ ...product });
        setIsEditing(false);
    };

    return (
        <div className="productTable-card">
            {/* Media Container */}
            <div className="productTable-media-container">
                {product.image ? (
                    <div className="main-image-wrapper">
                        <img
                            src={product.image}
                            alt="Main"
                            className="productTable-media"
                            style={{
                                width: "100%",

                                borderRadius: "4px",
                                objectFit: "cover",
                            }}
                        />
                        <button
                            onClick={() => handleRemoveImage(index, null, true)}
                            className="delete-image-btn"
                        >
                            ❌
                        </button>
                    </div>
                ) : (
                    <div className="no-image-placeholder">No Image</div>
                )}
                <button
                    onClick={() => mainImageRefs.current[index]?.click()}
                    className="add-image-btn"
                >
                    Add Main Image
                </button>
                <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    ref={(el) => (mainImageRefs.current[index] = el)}
                    onChange={(e) =>
                        handleFileUpload(index, e.target.files[0], true)
                    }
                />
            </div>

            {/* Minimal View */}
            {!isEditing ? (
                <div className="productTable-minimal">
                    <h3>{product.name}</h3>
                    <div className="price-info">
                        {product.mrp && (
                            <span className="mrp" style={{ textDecoration: "line-through" }}>
                                ₹{product.mrp}
                            </span>
                        )}
                        {product.sellingPrice && (
                            <span className="sellingPrice"> ₹{product.sellingPrice}</span>
                        )}
                        {product.discount && (
                            <span className="discount"> {product.discount}% Off</span>
                        )}
                    </div>
                    <button onClick={() => setIsEditing(true)} className="edit-btn">
                        Edit
                    </button>
                </div>
            ) : (
                // Expanded / Edit View
                <div className="productTable-edit">
                    <div className="info-field">
                        <label>Name:</label>
                        <textarea
                            rows="1"
                            value={formState.name || ""}
                            onChange={(e) => handleChange("name", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>SKU:</label>
                        <textarea
                            rows="1"
                            value={formState.sku || ""}
                            onChange={(e) => handleChange("sku", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>MRP:</label>
                        <textarea
                            rows="1"
                            value={formState.mrp || ""}
                            onChange={(e) => handleChange("mrp", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>Selling Price:</label>
                        <textarea
                            rows="1"
                            value={formState.sellingPrice || ""}
                            onChange={(e) => handleChange("sellingPrice", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>Discount:</label>
                        <textarea
                            rows="1"
                            value={formState.discount || ""}
                            onChange={(e) => handleChange("discount", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>In Stock:</label>
                        <select
                            value={formState.inStock ? "Yes" : "No"}
                            onChange={(e) =>
                                handleChange("inStock", e.target.value === "Yes")
                            }
                        >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                    <div className="info-field">
                        <label>Quantity Available:</label>
                        <textarea
                            rows="1"
                            value={formState.quantity || ""}
                            onChange={(e) => handleChange("quantity", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>Description:</label>
                        <textarea
                            rows="3"
                            value={formState.description || ""}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>Additional Info:</label>
                        <textarea
                            rows="3"
                            value={formState.additional_info || ""}
                            onChange={(e) =>
                                handleChange("additional_info", e.target.value)
                            }
                        />
                    </div>
                    <div className="info-field">
                        <label>Related Products:</label>
                        <textarea
                            rows="1"
                            placeholder="Comma-separated SKUs or IDs"
                            value={formState.related_products || ""}
                            onChange={(e) =>
                                handleChange("related_products", e.target.value)
                            }
                        />
                    </div>
                    <div className="info-field">
                        <label>Meta Title:</label>
                        <textarea
                            rows="1"
                            value={formState.metaTitle || ""}
                            onChange={(e) => handleChange("metaTitle", e.target.value)}
                        />
                    </div>
                    <div className="info-field">
                        <label>Meta Description:</label>
                        <textarea
                            rows="2"
                            value={formState.metaDescription || ""}
                            onChange={(e) =>
                                handleChange("metaDescription", e.target.value)
                            }
                        />
                    </div>
                    <div className="info-field">
                        <label>Meta Keywords:</label>
                        <textarea
                            rows="1"
                            value={formState.metaKeywords || ""}
                            onChange={(e) =>
                                handleChange("metaKeywords", e.target.value)
                            }
                        />
                    </div>

                    {/* Detail Images Section */}
                    <div className="detail-images">
                        {(product.detail_images || []).map((img, imgIndex) => (
                            <div
                                key={imgIndex}
                                className="detail-image-wrapper"
                                style={{ position: "relative", display: "inline-block" }}
                            >
                                <img
                                    src={img}
                                    alt="Detail"
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "3px",
                                        objectFit: "cover",
                                    }}
                                />
                                <button
                                    onClick={() =>
                                        handleRemoveImage(index, imgIndex, false)
                                    }
                                    className="delete-image-btn"
                                >
                                    ❌
                                </button>
                            </div>
                        ))}
                        <button
                            onClick={() => fileInputRefs.current[index]?.click()}
                            className="add-image-btn"
                        >
                            Add Detail Image
                        </button>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: "none" }}
                            ref={(el) => (fileInputRefs.current[index] = el)}
                            onChange={(e) => {
                                const files = Array.from(e.target.files);
                                files.forEach((file) => {
                                    handleFileUpload(index, file);
                                });
                            }}
                        />
                    </div>

                    <div className="edit-actions">
                        <button onClick={handleSave} className="save-btn">
                            Save
                        </button>
                        <button onClick={handleCancel} className="cancel-btn">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Button */}
            <button onClick={() => deleteRow(index)} className="delete-product-btn">
                Delete
            </button>
        </div>
    );
};

const ProductsCardView = ({
    displayedProducts,
    currentPage,
    itemsPerPage,
    handleFileUpload,
    handleRemoveImage,
    saveProductField,
    deleteRow,
    fileInputRefs,
    mainImageRefs,
}) => {
    return (
        <div className="products-card-view">
            {displayedProducts.map((product, i) => {
                const actualIndex = i + (currentPage - 1) * itemsPerPage;
                return (
                    <SingleProductCard
                        key={actualIndex}
                        product={product}
                        index={actualIndex}
                        handleFileUpload={handleFileUpload}
                        handleRemoveImage={handleRemoveImage}
                        saveProductField={saveProductField}
                        deleteRow={deleteRow}
                        fileInputRefs={fileInputRefs}
                        mainImageRefs={mainImageRefs}
                    />
                );
            })}
        </div>
    );
};

export default ProductsCardView;
